import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  LinearProgress,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MapIcon from '@mui/icons-material/Map'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { getYards, createYard, updateYard, deleteYard } from '../services/api'
import { getAccounts, Account } from '../services/accounts'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { useJsApiLoader, GoogleMap, MarkerF, OverlayView } from '@react-google-maps/api'
import { getGoogleMapsApiKey } from '../config/maps'
import { getMapsLanguage } from '../components/common/mapsLanguage'

const MAP_LIBRARIES: ('places')[] = ['places']

const GoogleAutocompleteFix = createGlobalStyle`
  .pac-container {
    z-index: 1500 !important;
  }
`

const Container = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
  padding-bottom: ${props => props.theme.spacing.xl};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.xxxlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.text} 0%, ${props => props.theme.colors.textSecondary} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: ${props => props.theme.typography.lineHeight.tight};
`

const TableWrapper = styled(TableContainer)`
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
`

const Row = styled(TableRow)`
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`

interface Yard {
  _id?: string
  id?: string
  name: string
  address: string
  latitude?: number
  longitude?: number
  gateCode?: string
  accountId?: string
  // Summary fields computed by GET /yards
  hiveCount?: number
  deviceStatus?: 'good' | 'warning' | 'critical' | null
  gatewayCount?: number
  deviceCount?: number
  activeDeviceCount?: number
  lastVisitDate?: string | null
  avgStrengthPct?: number | null
  strengthSampleSize?: number
  queenlessCount?: number
  hiveRates?: { healthy: number; weak: number; dead: number }
  fullStrengthFrames?: number
}

const GOOD = '#10b981'
const WARN = '#f59e0b'
const BAD = '#ef4444'
const NONE = '#9e9e9e'

const DEVICE_STATUS_COLOR = { good: GOOD, warning: WARN, critical: BAD }

// 60% = the "medium" colony threshold (6 of 10 occupied frames)
function strengthColor(pct: number): string {
  return pct >= 80 ? GOOD : pct >= 60 ? WARN : BAD
}

// Inspections run every 1–2 weeks in season
function visitAgeColor(days: number): string {
  return days > 30 ? BAD : days > 14 ? WARN : 'inherit'
}

// Demo only: yards with no graded or frame-counted visit get a stable made-up
// strength (45–90%, from the yard id) so the column isn't empty. Shown dimmed
// with a "demo value" tooltip. Set to false to show '-' instead.
const DEMO_STRENGTH_PLACEHOLDERS = true

function demoStrengthPct(yard: Yard): number {
  const key = String(yard._id || yard.id || yard.name)
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return 45 + (h % 46)
}

function daysSince(day: string): number {
  const [y, m, d] = day.split('-').map(Number)
  const then = new Date(y, m - 1, d).getTime()
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  return Math.max(0, Math.round((today - then) / 86400000))
}

const DEFAULT_CENTER = { lat: 27.9944, lng: -81.7603 }
const DEFAULT_ZOOM = 7

function YardsScreen(): React.JSX.Element {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [yards, setYards] = useState<Yard[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [selectedYard, setSelectedYard] = useState<Yard | null>(null)
  const [mapYard, setMapYard] = useState<Yard | null>(null)
  const [editForm, setEditForm] = useState({ name: '', address: '', gateCode: '' })
  const [createForm, setCreateForm] = useState({
    name: '', address: '', gateCode: '', latitude: '', longitude: '', accountId: '',
  })
  const [saving, setSaving] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  })

  // Google Maps
  const googleMapsApiKey = getGoogleMapsApiKey()
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey,
    libraries: MAP_LIBRARIES,
    // Frozen per page load — the loader throws if options change between mounts
    language: getMapsLanguage(),
  })
  const mapsAvailable = isLoaded && googleMapsApiKey.length > 0

  // User's current location
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude })
        },
        () => { /* geolocation denied or unavailable, keep default */ }
      )
    }
  }, [])

  // Create dialog map state
  const createAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [createMapCenter, setCreateMapCenter] = useState(DEFAULT_CENTER)
  const [createMarker, setCreateMarker] = useState<{ lat: number; lng: number } | null>(null)

  // Callback ref: attaches Google Places Autocomplete when the input mounts
  const createAddressRef = useCallback((node: HTMLInputElement | null) => {
    if (createAutocompleteRef.current) {
      google.maps.event.clearInstanceListeners(createAutocompleteRef.current)
      createAutocompleteRef.current = null
    }
    if (!node || !isLoaded) return

    const autocomplete = new google.maps.places.Autocomplete(node, {
      types: ['address'],
    })
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        setCreateForm(prev => ({
          ...prev,
          address: place.formatted_address || prev.address,
          latitude: String(lat),
          longitude: String(lng),
        }))
        setCreateMapCenter({ lat, lng })
        setCreateMarker({ lat, lng })
      }
    })
    createAutocompleteRef.current = autocomplete
  }, [isLoaded])

  // Edit dialog map state
  const editAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [editMapCenter, setEditMapCenter] = useState(DEFAULT_CENTER)
  const [editMarker, setEditMarker] = useState<{ lat: number; lng: number } | null>(null)

  // Callback ref: attaches Google Places Autocomplete when the input mounts
  const editAddressRef = useCallback((node: HTMLInputElement | null) => {
    if (editAutocompleteRef.current) {
      google.maps.event.clearInstanceListeners(editAutocompleteRef.current)
      editAutocompleteRef.current = null
    }
    if (!node || !isLoaded) return

    const autocomplete = new google.maps.places.Autocomplete(node, {
      types: ['address'],
    })
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()
      if (place.geometry?.location) {
        const lat = place.geometry.location.lat()
        const lng = place.geometry.location.lng()
        setEditForm(prev => ({
          ...prev,
          address: place.formatted_address || prev.address,
        }))
        setEditMapCenter({ lat, lng })
        setEditMarker({ lat, lng })
      }
    })
    editAutocompleteRef.current = autocomplete
  }, [isLoaded])

  async function loadData(): Promise<void> {
    try {
      setLoading(true)
      const [yardsRes, accountsRes] = await Promise.all([getYards(), getAccounts()])
      setYards(yardsRes.data)
      setAccounts(accountsRes.data)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const accountNameById = React.useMemo(() => {
    const map = new Map<string, string>()
    accounts.forEach(a => {
      map.set(a.id, a.name)
    })
    return map
  }, [accounts])

  function handleRowClick(yard: Yard): void {
    const yardId = yard._id || yard.id
    if (yardId) navigate(`/yards/${yardId}`)
  }

  function handleEditClick(e: React.MouseEvent, yard: Yard): void {
    e.stopPropagation()
    setSelectedYard(yard)
    setEditForm({
      name: yard.name,
      address: yard.address,
      gateCode: yard.gateCode || '',
    })
    if (yard.latitude && yard.longitude) {
      const pos = { lat: yard.latitude, lng: yard.longitude }
      setEditMapCenter(pos)
      setEditMarker(pos)
    } else {
      setEditMapCenter(userLocation || DEFAULT_CENTER)
      setEditMarker(null)
    }
    setEditDialogOpen(true)
  }

  function handleDeleteClick(e: React.MouseEvent, yard: Yard): void {
    e.stopPropagation()
    setSelectedYard(yard)
    setDeleteDialogOpen(true)
  }

  function handleOpenCreate(): void {
    setCreateForm({ name: '', address: '', gateCode: '', latitude: '', longitude: '', accountId: '' })
    setCreateMarker(null)
    setCreateMapCenter(userLocation || DEFAULT_CENTER)
    setCreateDialogOpen(true)
  }

  async function handleEditSave(): Promise<void> {
    if (!selectedYard) return
    const yardId = selectedYard._id || selectedYard.id
    if (!yardId) return

    setSaving(true)
    try {
      const payload: Record<string, any> = { ...editForm }
      if (editMarker) {
        payload.latitude = editMarker.lat
        payload.longitude = editMarker.lng
      }
      await updateYard(yardId, payload)
      setEditDialogOpen(false)
      setSnackbar({ open: true, message: t('yard.updateSuccess'), severity: 'success' })
      await loadData()
    } catch (error) {
      console.error('Error updating yard:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteConfirm(): Promise<void> {
    if (!selectedYard) return
    const yardId = selectedYard._id || selectedYard.id
    if (!yardId) return

    setSaving(true)
    try {
      await deleteYard(yardId)
      setDeleteDialogOpen(false)
      setSnackbar({ open: true, message: t('yard.deleteSuccess'), severity: 'success' })
      await loadData()
    } catch (error) {
      console.error('Error deleting yard:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleCreateSave(): Promise<void> {
    setSaving(true)
    try {
      const payload: Record<string, any> = {
        name: createForm.name,
        address: createForm.address,
        gateCode: createForm.gateCode || undefined,
      }
      if (createForm.latitude) payload.latitude = parseFloat(createForm.latitude)
      if (createForm.longitude) payload.longitude = parseFloat(createForm.longitude)
      if (createForm.accountId) payload.accountId = createForm.accountId
      await createYard(payload)
      setCreateDialogOpen(false)
      setCreateForm({ name: '', address: '', gateCode: '', latitude: '', longitude: '', accountId: '' })
      setCreateMarker(null)
      setCreateMapCenter(DEFAULT_CENTER)
      setSnackbar({ open: true, message: t('yard.createSuccess'), severity: 'success' })
      await loadData()
    } catch (error) {
      console.error('Error creating yard:', error)
      setSnackbar({ open: true, message: t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  function onCreateMapClick(e: google.maps.MapMouseEvent): void {
    if (e.latLng) {
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      setCreateForm(prev => ({
        ...prev,
        latitude: String(lat),
        longitude: String(lng),
      }))
      setCreateMarker({ lat, lng })
    }
  }

  function onEditMapClick(e: google.maps.MapMouseEvent): void {
    if (e.latLng) {
      const lat = e.latLng.lat()
      const lng = e.latLng.lng()
      setEditMarker({ lat, lng })
    }
  }

  const showAccountSelector = accounts.length > 1

  return (
    <Container>
      <GoogleAutocompleteFix />
      <Header>
        <Title>{t('yard.list')}</Title>
        <Button variant="contained" onClick={handleOpenCreate}>
          {t('yard.create')}
        </Button>
      </Header>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <Paper sx={{ borderRadius: '8px' }}>
          <TableWrapper>
            <Table aria-label={t('yard.list')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('yard.name')}</TableCell>
                  <TableCell align="center">{t('yard.table.hives')}</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>{t('yard.table.avgStrength')}</TableCell>
                  <TableCell align="center">{t('yard.table.devices')}</TableCell>
                  <TableCell>{t('yard.table.lastVisit')}</TableCell>
                  <TableCell>{t('yard.address')}</TableCell>
                  <TableCell align="center">{t('yard.table.location')}</TableCell>
                  <TableCell>{t('yard.gateCode')}</TableCell>
                  {showAccountSelector && <TableCell>{t('yard.account')}</TableCell>}
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yards.map(yard => (
                  <Row key={yard.id || yard._id} onClick={() => handleRowClick(yard)}>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {yard.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ minWidth: 140 }}>
                      {yard.hiveRates ? (() => {
                        const { healthy, weak, dead } = yard.hiveRates
                        const total = healthy + weak + dead
                        const parts = [
                          { n: healthy, color: GOOD, label: t('yard.table.good') },
                          { n: weak, color: WARN, label: t('yard.table.medium') },
                          { n: dead, color: BAD, label: t('yard.table.dead') },
                        ]
                        return (
                          <Tooltip
                            title={t('yard.table.hiveStatusTooltip', { total, good: healthy, medium: weak, dead })}
                          >
                            <Box data-testid="hive-status">
                              <Typography variant="body2" fontWeight={600}>{total}</Typography>
                              <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', my: 0.5, bgcolor: 'action.hover' }}>
                                {parts.filter(p => p.n > 0).map(p => (
                                  <Box key={p.label} sx={{ width: `${(p.n / total) * 100}%`, bgcolor: p.color }} />
                                ))}
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, fontSize: 12 }}>
                                {parts.map(p => (
                                  <Box key={p.label} component="span" sx={{ color: p.color, fontWeight: 600 }}>
                                    {p.n}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Tooltip>
                        )
                      })() : (yard.hiveCount || '-')}
                      {!!yard.queenlessCount && (
                        <Chip
                          size="small"
                          label={t('yard.table.queenless', { count: yard.queenlessCount })}
                          sx={{ ml: 1, bgcolor: BAD, color: '#fff', fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const isDemo = yard.avgStrengthPct == null && DEMO_STRENGTH_PLACEHOLDERS
                        const pct = yard.avgStrengthPct ?? (isDemo ? demoStrengthPct(yard) : null)
                        if (pct == null) return '-'
                        return (
                        <Tooltip
                          title={isDemo ? t('yard.table.demoValue') : t('yard.table.avgStrengthTooltip', {
                            full: yard.fullStrengthFrames ?? 10,
                            count: yard.strengthSampleSize ?? 0,
                          })}
                        >
                          <Box
                            data-demo={isDemo || undefined}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, opacity: isDemo ? 0.5 : 1, fontStyle: isDemo ? 'italic' : 'normal' }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': { bgcolor: strengthColor(pct) },
                              }}
                            />
                            <Typography variant="body2" fontWeight={600} sx={{ minWidth: 40 }}>
                              <bdi>{pct}%</bdi>
                            </Typography>
                          </Box>
                        </Tooltip>
                        )
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      {yard.deviceCount ? (
                        <Tooltip
                          title={t('yard.table.devicesTooltip', {
                            active: yard.activeDeviceCount ?? 0,
                            total: yard.deviceCount,
                            gateways: yard.gatewayCount ?? 0,
                            status: yard.deviceStatus ? t(`dashboard.marker.deviceStatus.${yard.deviceStatus}`) : '-',
                          })}
                        >
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Box
                              component="span"
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: yard.deviceStatus ? DEVICE_STATUS_COLOR[yard.deviceStatus] : NONE,
                              }}
                            />
                            <bdi dir="ltr">{`${yard.activeDeviceCount ?? 0}/${yard.deviceCount}`}</bdi>
                          </Box>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      {yard.lastVisitDate ? (() => {
                        const days = daysSince(yard.lastVisitDate)
                        return (
                          <Tooltip title={new Date(`${yard.lastVisitDate}T00:00:00`).toLocaleDateString(i18n.language)}>
                            <Typography variant="body2" sx={{ color: visitAgeColor(days), fontWeight: days > 14 ? 600 : 400 }}>
                              {days === 0 ? t('yard.table.today') : t('devices.agoDays', { count: days })}
                            </Typography>
                          </Tooltip>
                        )
                      })() : '-'}
                    </TableCell>
                    <TableCell>{yard.address}</TableCell>
                    <TableCell align="center">
                      {yard.latitude && yard.longitude ? (
                        <Tooltip title={t('yard.table.showOnMap')}>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label={t('yard.table.showOnMap')}
                            onClick={e => { e.stopPropagation(); setMapYard(yard) }}
                          >
                            <MapIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>{yard.gateCode || '-'}</TableCell>
                    {showAccountSelector && (
                      <TableCell>
                        {yard.accountId ? accountNameById.get(String(yard.accountId)) || '-' : '-'}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Tooltip title={t('yard.edit')}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={e => handleEditClick(e, yard)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('yard.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={e => handleDeleteClick(e, yard)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </Row>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        </Paper>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('yard.create')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('form.label.name')}
            fullWidth
            value={createForm.name}
            onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
          />
          {showAccountSelector && (
            <FormControl fullWidth margin="dense">
              <InputLabel>{t('yard.selectAccount')}</InputLabel>
              <Select
                value={createForm.accountId}
                onChange={e => setCreateForm(prev => ({ ...prev, accountId: e.target.value as string }))}
                label={t('yard.selectAccount')}
              >
                {accounts.map(account => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            inputRef={createAddressRef}
            margin="dense"
            label={t('form.label.address')}
            fullWidth
            placeholder={t('yard.searchAddress')}
            value={createForm.address}
            onChange={e => setCreateForm(prev => ({ ...prev, address: e.target.value }))}
          />
          {mapsAvailable ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {t('yard.clickMapToPin')}
              </Typography>
              <Box sx={{ width: '100%', height: 350, borderRadius: 1, overflow: 'hidden', mt: 1, mb: 1, border: '1px solid #e0e0e0' }}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={createMapCenter}
                  zoom={createMarker ? 15 : (userLocation ? 12 : DEFAULT_ZOOM)}
                  onClick={onCreateMapClick}
                >
                  {createMarker && <MarkerF position={createMarker} />}
                  {yards.filter(y => y.latitude && y.longitude).map((y, idx) => (
                    <OverlayView
                      key={y._id || y.id}
                      position={{ lat: y.latitude!, lng: y.longitude! }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translate(-50%, -100%)', cursor: 'pointer', filter: 'drop-shadow(0 3px 7px rgba(245,160,0,0.5))' }}>
                        <svg width="44" height="54" viewBox="0 0 48 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <defs>
                            <linearGradient id={`ys-grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#f9c200" />
                              <stop offset="100%" stopColor="#f5a000" />
                            </linearGradient>
                          </defs>
                          <path d="M24 2C13.51 2 5 10.51 5 21c0 13.5 19 35 19 35s19-21.5 19-35C43 10.51 34.49 2 24 2z" fill={`url(#ys-grad-${idx})`} />
                          <circle cx="24" cy="20" r="13" fill="white" fillOpacity="0.18" />
                          <circle cx="24" cy="20" r="11" fill="white" fillOpacity="0.22" />
                          <text x="24" y="24" textAnchor="middle" fill="white" fontSize="14" fontFamily="Arial, sans-serif">🐝</text>
                        </svg>
                        <div style={{
                          marginTop: -2,
                          background: 'rgba(20,20,20,0.82)',
                          color: '#fff',
                          fontSize: 10,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 10,
                          whiteSpace: 'nowrap',
                          maxWidth: 110,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          border: '1px solid rgba(249,194,0,0.4)',
                          boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                        }}>
                          {y.name}
                        </div>
                      </div>
                    </OverlayView>
                  ))}
                </GoogleMap>
              </Box>
              {createMarker && (
                <Typography variant="caption" color="text.secondary">
                  {t('yard.coordinatesValue', { lat: createMarker.lat.toFixed(6), lng: createMarker.lng.toFixed(6) })}
                </Typography>
              )}
            </>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                margin="dense"
                label={t('yard.latitude')}
                fullWidth
                type="number"
                value={createForm.latitude}
                onChange={e => setCreateForm(prev => ({ ...prev, latitude: e.target.value }))}
              />
              <TextField
                margin="dense"
                label={t('yard.longitude')}
                fullWidth
                type="number"
                value={createForm.longitude}
                onChange={e => setCreateForm(prev => ({ ...prev, longitude: e.target.value }))}
              />
            </Box>
          )}
          <TextField
            margin="dense"
            label={t('form.label.gateCode')}
            fullWidth
            value={createForm.gateCode}
            onChange={e => setCreateForm(prev => ({ ...prev, gateCode: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleCreateSave} variant="contained" disabled={saving || !createForm.name || !createForm.address}>
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('yard.edit')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('form.label.name')}
            fullWidth
            value={editForm.name}
            onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
          />
          <TextField
            inputRef={editAddressRef}
            margin="dense"
            label={t('form.label.address')}
            fullWidth
            placeholder={t('yard.searchAddress')}
            value={editForm.address}
            onChange={e => setEditForm(prev => ({ ...prev, address: e.target.value }))}
          />
          {mapsAvailable && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ fontSize: 14, mr: 0.5 }} />
                {t('yard.clickMapToPin')}
              </Typography>
              <Box sx={{ width: '100%', height: 350, borderRadius: 1, overflow: 'hidden', mt: 1, mb: 1, border: '1px solid #e0e0e0' }}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={editMapCenter}
                  zoom={editMarker ? 15 : DEFAULT_ZOOM}
                  onClick={onEditMapClick}
                >
                  {editMarker && <MarkerF position={editMarker} />}
                </GoogleMap>
              </Box>
              {editMarker && (
                <Typography variant="caption" color="text.secondary">
                  {t('yard.coordinatesValue', { lat: editMarker.lat.toFixed(6), lng: editMarker.lng.toFixed(6) })}
                </Typography>
              )}
            </>
          )}
          <TextField
            margin="dense"
            label={t('form.label.gateCode')}
            fullWidth
            value={editForm.gateCode}
            onChange={e => setEditForm(prev => ({ ...prev, gateCode: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={saving || !editForm.name || !editForm.address}>
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location Dialog — topographic map with the yard's pin */}
      <Dialog open={!!mapYard} onClose={() => setMapYard(null)} maxWidth="md" fullWidth>
        <DialogTitle>{mapYard?.name}</DialogTitle>
        <DialogContent>
          {mapYard?.latitude && mapYard?.longitude && (
            <>
              {mapsAvailable ? (
                <Box sx={{ width: '100%', height: 450, borderRadius: 1, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: mapYard.latitude, lng: mapYard.longitude }}
                    zoom={14}
                    options={{ mapTypeId: 'terrain', mapTypeControl: true }}
                  >
                    <MarkerF position={{ lat: mapYard.latitude, lng: mapYard.longitude }} title={mapYard.name} />
                  </GoogleMap>
                </Box>
              ) : (
                <Typography color="text.secondary">{t('yard.table.mapUnavailable')}</Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {mapYard.address} · <bdi dir="ltr">{`${mapYard.latitude.toFixed(6)}, ${mapYard.longitude.toFixed(6)}`}</bdi>
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapYard(null)}>{t('settings.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('yard.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('yard.confirmDelete', { name: selectedYard?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
            {t('button.cancel.text')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            {saving ? t('common.loading') : t('button.delete.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default YardsScreen
